import React from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleLeft, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import Container from 'components/shared/Container';
import { handleBackNavigation } from 'lib/utils/navigationUtils';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import pointerOnHover from 'lib/css/pointerOnHover';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import UPBProductDetailsPage from 'components/upb_viewer/UPBProductDetailsPage';
import {
  PRODUCT_ENRICHMENT_PDF_APPROVE,
  PRODUCT_ENRICHMENT_PDF_PRODUCT_DATA,
  PRODUCT_ENRICHMENT_PDF_URL_BY_ID,
} from 'lib/networking/endpoints';
import CustomContainer from 'components/shared/Container';

function PDFProductDetailsView() {
  const { resourceId, pdfUrlId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const handleGoBack = () => {
    // Determine fallback based on resourceId
    const fallback = resourceId
      ? `/product-enrichment-resources/pdf-qa/${resourceId}`
      : '/product-enrichment-resources';

    handleBackNavigation(history, location, fallback);
  };

  const { data: { data: pdfMetadata } = {}, loading: loadingPDFMetadata } = useGet(
    `${PRODUCT_ENRICHMENT_PDF_URL_BY_ID}/${pdfUrlId}`,
    {},
    !pdfUrlId,
  );

  const {
    data: { data: productData = {} } = {},
    loading: loadingProductData,
    error: productDataError,
  } = useGet(
    PRODUCT_ENRICHMENT_PDF_PRODUCT_DATA,
    {
      resource_id: resourceId,
      pdf_url_id: pdfUrlId,
    },
    !pdfUrlId,
  );

  // Approval endpoint
  const { postData: approvePDF, loading: approvingPDF } = usePost(
    PRODUCT_ENRICHMENT_PDF_APPROVE,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'PDF approval status updated successfully',
      }).then(() => {
        history.push(`/product-enrichment-resources/pdf-qa/${resourceId}`);
      });
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred while updating approval status: ${error?.response?.data?.message}`,
      });
    },
  );

  const handleApprove = approved => {
    approvePDF({
      pdf_url_id: pdfUrlId,
      approved,
      approved_attributes: productData.excel_data || productData,
    });
  };

  if (loadingProductData || loadingPDFMetadata) {
    return <LoadingSpinner />;
  }

  if (productDataError) {
    return (
      <CustomContainer>
        <h3>Error loading product data</h3>
        <button className="btn btn-primary" onClick={handleGoBack}>
          Go Back
        </button>
      </CustomContainer>
    );
  }

  // Check if PDF is already approved
  const isPdfApproved = pdfMetadata?.pdf_approval_status === true;

  return (
    <Container>
      <div className="mb-3 d-flex justify-content-between">
        <h3 className="font-weight-bold">
          <FontAwesomeIcon
            className={classNames(pointerOnHover, 'mr-2')}
            icon={faArrowCircleLeft}
            onClick={handleGoBack}
          />
          PDF Document Details
        </h3>
        <div>
          {!isPdfApproved && (
            <>
              <Button
                variant="success"
                className="mr-2"
                onClick={() => handleApprove(true)}
                disabled={approvingPDF}
              >
                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                Approve
              </Button>
              <Button variant="danger" onClick={() => handleApprove(false)} disabled={approvingPDF}>
                <FontAwesomeIcon icon={faTimes} className="mr-1" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>

      <Row className={css(styles.detailsContainer)}>
        <Col md={7}>
          {/* UPB Product Details View */}
          {productData && (
            <div className={css(styles.upbContainer)}>
              <h4 className="mt-4">UPB Product Details View</h4>
              <UPBProductDetailsPage customData={productData} hideHeader={false} />
            </div>
          )}
        </Col>
        <Col md={5}>
          <div className={css(styles.infoContainer)}>
            <h4>Document Information</h4>
            <Table bordered>
              <tbody>
                <tr>
                  <th width="30%">Product Name</th>
                  <td>
                    {pdfMetadata?.manufacturer_product_name || pdfMetadata?.vendor_product_name}
                  </td>
                </tr>
                <tr>
                  <th>SKU</th>
                  <td>{pdfMetadata?.manufacturer_sku || pdfMetadata?.vendor_sku}</td>
                </tr>
                <tr>
                  <th>Entity Type</th>
                  <td>{pdfMetadata?.product_type || 'N/A'}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    {pdfMetadata?.pdf_approval_status ? (
                      <span className="badge badge-success">Approved</span>
                    ) : (
                      <span className="badge badge-danger">Not Approved</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Uploaded By</th>
                  <td>{pdfMetadata?.uploaded_by || 'N/A'}</td>
                </tr>
                {isPdfApproved && (
                  <tr>
                    <th>Approved By</th>
                    <td>{pdfMetadata?.approved_by || 'N/A'}</td>
                  </tr>
                )}
              </tbody>
            </Table>

            {productData?.excel_data && (
              <>
                <h4 className="mt-4">Excel Data</h4>
                <div className={css(styles.excelDataContainer)}>
                  <Table bordered striped>
                    <tbody>
                      {Object.entries(productData.excel_data).map(([key, value]) => (
                        <tr key={key}>
                          <th width="30%">{key}</th>
                          <td>{value || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

const styles = StyleSheet.create({
  detailsContainer: {
    marginBottom: '30px',
  },
  pdfContainer: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '20px',
  },
  pdfFrame: {
    border: 'none',
    width: '100%',
    height: '100%',
  },
  upbContainer: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '15px',
    backgroundColor: '#fff',
  },
  infoContainer: {
    backgroundColor: '#fff',
    padding: '15px',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  excelDataContainer: {
    maxHeight: '300px',
    overflowY: 'auto',
    border: '1px solid #eee',
    borderRadius: '4px',
  },
});

PDFProductDetailsView.propTypes = {
  resourceId: PropTypes.string,
  pdfUrlId: PropTypes.string,
};

export default PDFProductDetailsView;
